/**
 * Утилиты для работы с push-уведомлениями
 */

// Ключ для хранения статуса разрешения
const PERMISSION_STATUS_KEY = 'push_notification_permission';

/**
 * Проверяет доступность API уведомлений в браузере
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
  return 'Notification' in window;
};

/**
 * Получает текущий статус разрешения на уведомления
 * @returns {string} 'default' | 'granted' | 'denied'
 */
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
};

/**
 * Проверяет, нужно ли запросить разрешение
 * @returns {boolean}
 */
export const shouldRequestPermission = () => {
  if (!isNotificationSupported()) {
    return false;
  }
  return Notification.permission === 'default';
};

/**
 * Запрашивает разрешение на отправку уведомлений
 * @returns {Promise<string>} 'granted' | 'denied' | 'default'
 */
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.warn('[Notifications] Push notifications are not supported');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    localStorage.setItem(PERMISSION_STATUS_KEY, permission);
    console.log('[Notifications] Permission:', permission);
    return permission;
  } catch (error) {
    console.error('[Notifications] Error requesting permission:', error);
    return 'denied';
  }
};

/**
 * Проверяет, были ли уведомления отключены пользователем
 * @returns {boolean}
 */
export const isNotificationDenied = () => {
  return getNotificationPermission() === 'denied';
};

/**
 * Проверяет, разрешены ли уведомления
 * @returns {boolean}
 */
export const isNotificationGranted = () => {
  return getNotificationPermission() === 'granted';
};

/**
 * Отправляет push-уведомление
 * @param {Object} options - Параметры уведомления
 * @param {string} options.title - Заголовок уведомления
 * @param {string} options.body - Текст уведомления
 * @param {string} options.icon - URL иконки (опционально)
 * @param {string} options.tag - Тег для группировки уведомлений (опционально)
 * @param {Object} options.data - Дополнительные данные (опционально)
 * @returns {Notification|null} Объект уведомления или null
 */
export const sendNotification = (options) => {
  if (!isNotificationSupported()) {
    console.warn('[Notifications] Push notifications are not supported');
    return null;
  }

  if (!isNotificationGranted()) {
    console.warn('[Notifications] Permission not granted');
    return null;
  }

  // Проверяем, находится ли страница в фокусе
  // Не показываем уведомления, если пользователь уже смотрит на страницу
  if (document.hasFocus() && document.visibilityState === 'visible') {
    console.log('[Notifications] Page is focused, skipping notification');
    return null;
  }

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || '/logo.png', // Используем иконку приложения по умолчанию
      badge: options.icon || '/logo.png',
      tag: options.tag || 'chat-message',
      requireInteraction: false,
      silent: false,
      data: options.data || {},
    });

    // Автоматически закрывать уведомление через 5 секунд
    setTimeout(() => {
      notification.close();
    }, 5000);

    // При клике на уведомление - фокусируем окно
    notification.onclick = () => {
      window.focus();
      notification.close();
      
      // Если есть callback в data, вызываем его
      if (options.data?.onClick) {
        options.data.onClick();
      }
    };

    return notification;
  } catch (error) {
    console.error('[Notifications] Error sending notification:', error);
    return null;
  }
};

/**
 * Отправляет уведомление о новом сообщении в чате
 * @param {Object} message - Объект сообщения
 * @param {string} message.text - Текст сообщения
 * @param {string} message.type - Тип сообщения (admin, system, etc.)
 */
export const sendChatMessageNotification = (message) => {
  let title = 'Новое сообщение';
  
  if (message.type === 'admin_message' || message.type === 'admin') {
    title = 'Сообщение от администратора';
  } else if (message.type === 'system') {
    title = 'Системное сообщение';
  }

  // Ограничиваем длину текста для уведомления
  let body = message.text || 'Получено новое сообщение';
  if (body.length > 100) {
    body = body.substring(0, 97) + '...';
  }

  sendNotification({
    title,
    body,
    tag: 'chat-message',
    data: {
      messageId: message.id,
      messageType: message.type,
    },
  });
};

export default {
  isNotificationSupported,
  getNotificationPermission,
  shouldRequestPermission,
  requestNotificationPermission,
  isNotificationDenied,
  isNotificationGranted,
  sendNotification,
  sendChatMessageNotification,
};

